import React, {useEffect, useState} from "react";
import "./testSidebar.css";
import {Menu, MenuItem, Sidebar, SubMenu, useProSidebar} from "react-pro-sidebar";
import {Link, useLocation} from "react-router-dom";
import {
    AutoGraphOutlined,
    BackupOutlined,
    BusinessOutlined,
    ChatBubbleOutlineOutlined,
    DashboardOutlined,
    ElderlyOutlined,
    GroupsOutlined,
    LogoutOutlined,
    MonetizationOnOutlined,
    MoneyOffCsredOutlined,
    PaymentsOutlined,
    PeopleOutlined,
    SettingsOutlined,
    AccountBalanceWalletOutlined,
    AccountBalanceOutlined,
    MenuBookOutlined,
    ReceiptOutlined,
    AssessmentOutlined,
} from "@mui/icons-material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import {useMediaQuery, Avatar, Typography, Divider, Tooltip, Box, IconButton} from "@mui/material";
import CryptoJS from "crypto-js";
import logo from "../../assets/react-logo-no-background.svg";
import {KEY} from "../../Constants";
import {permissionsApi} from "../../services/api";
import {getLandlordId, getSubUserId, isSubUser} from "../../services/api/client";

// Define color constants for consistency
const COLORS = {
    primary: "#2C5283",
    secondary: "#E2684F",
    tertiary: "#4CAF50",
    background: "#FFFFFF",
    backgroundAlt: "#F3F7FA",
    hover: "#EDF2F7",
    active: "#E1EDF7",
    text: "#2C3E50",
    textLight: "#6B7C93",
    border: "#E2E8F0",
    divider: "#EDF2F7",
};

export default function TestSidebar({sidebarOpen = true, setSidebarOpen, onCollapse}) {
    const isMobile = useMediaQuery("(max-width:800px)");
    const {collapseSidebar, collapsed, toggleSidebar} = useProSidebar();
    const location = useLocation();

    const decryptLocalStorage = () => {
        try {
            const encryptedData = localStorage.getItem("myMap");
            if (!encryptedData) return "";
            const decryptedData = CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8);

            return JSON.parse(decryptedData);
        } catch {
            return null;
        }
    };

    const [originalText, setOriginalText] = useState(decryptLocalStorage());
    const [expandedSubmenus, setExpandedSubmenus] = useState({
        tenants: false,
        money: false,
        accounting: false,
        accountingReports: false,
    });
    const [userPermissions, setUserPermissions] = useState([]);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    // Get user info from originalText
    const userName = originalText && originalText[1] ? originalText[1][1] : "User";
    const userRole = originalText && originalText[2] ? originalText[2][1] : "Property Manager";

    // Fetch user permissions on mount
    useEffect(() => {
        const fetchUserPermissions = async () => {
            try {
                const landlordId = getLandlordId();
                const subUserId = getSubUserId();

                console.log("landlord id: " + landlordId);
                console.log("sub_user_id: " + subUserId);

                // If no sub_user_id, this is a landlord - they have all permissions
                if (!subUserId) {
                    console.log("User is a landlord - granting all permissions.");
                    setPermissionsLoaded(true);
                    return;
                }

                // This is a sub-user, fetch their effective permissions
                if (!landlordId) {
                    console.error('Missing landlord_id for sub-user');
                    setPermissionsLoaded(true);
                    return;
                }

                console.log("Fetching permissions for sub-user...");
                const response = await permissionsApi.getEffective({
                    // landlord_id: landlordId,
                    sub_user_id: subUserId,
                });

                if (response.ok) {
                    // Extract permission names from the response
                    const permissions = response.data.permissions || [];
                    const permissionNames = permissions.map(p => p.permission_name);
                    console.log("Sub-user permissions loaded:", permissionNames);
                    setUserPermissions(permissionNames);
                } else {
                    console.error('Failed to fetch permissions:', response);
                }
            } catch (error) {
                console.error('Error fetching permissions:', error);
            } finally {
                setPermissionsLoaded(true);
            }
        };

        fetchUserPermissions();
    }, []);

    // Check if user has a specific permission
    const hasPermission = (permissionName) => {
        // If no sub_user_id, this is a landlord - they have all permissions
        const subUserId = getSubUserId();
        if (!subUserId) {
            return true;
        }
        // Sub-users need to have the permission in their list
        return userPermissions.includes(permissionName);
    };

    // Check if path is active (for styling)
    const isActive = (path) => {
        if (typeof path === 'string') {
            return location.pathname === path;
        } else if (Array.isArray(path)) {
            return path.some(p => location.pathname.includes(p));
        }
        return false;
    };

    // Handle submenu expansion
    const handleSubmenuExpand = (submenu) => {
        setExpandedSubmenus({
            ...expandedSubmenus,
            [submenu]: !expandedSubmenus[submenu],
        });
    };

    // Simplified effect to sync sidebar state
    useEffect(() => {
        // For mobile: when sidebarOpen changes, sync with pro-sidebar
        if (isMobile) {
            if (sidebarOpen && collapsed) {
                toggleSidebar();
            } else if (!sidebarOpen && !collapsed) {
                collapseSidebar();
            }
        } else {
            // For desktop: when sidebarOpen changes, sync with pro-sidebar
            if (sidebarOpen && collapsed) {
                toggleSidebar();
            } else if (!sidebarOpen && !collapsed) {
                collapseSidebar();
            }
        }

        // Notify parent about collapse state
        onCollapse(collapsed);
    }, [sidebarOpen, isMobile]);

    // Handle internal sidebar toggle
    const handleToggle = () => {
        setSidebarOpen(!sidebarOpen);
        if (sidebarOpen) {
            collapseSidebar();
        } else {
            toggleSidebar();
        }
    };

    const logout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    const menuItemStyles = {
        root: {
            fontSize: "14px",
            fontWeight: 500,
            zIndex: 1100,
        },
        button: ({level, active}) => ({
            color: active ? COLORS.primary : COLORS.text,
            backgroundColor: active ? COLORS.active : "transparent",
            borderLeft: active ? `3px solid ${COLORS.primary}` : "3px solid transparent",
            borderRadius: "0",
            fontWeight: active ? 600 : 500,
            padding: level === 0 ? "10px 16px" : "8px 16px 8px 32px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
                backgroundColor: COLORS.hover,
                borderLeft: active
                    ? `3px solid ${COLORS.primary}`
                    : `3px solid ${COLORS.secondary}`,
            },
        }),
        icon: {
            color: COLORS.primary,
            fontSize: "1.2rem",
        },
        SubMenuExpandIcon: {
            color: COLORS.text,
        },
        subMenuContent: ({level}) => ({
            backgroundColor: COLORS.backgroundAlt,
        }),
        label: ({open, active}) => ({
            fontWeight: active ? 600 : 500,
        }),
    };

    // Custom Sidebar Header component
    const SidebarHeader = () => (
        <Box
            sx={{
                padding: collapsed ? "16px 8px" : "16px",
                display: "flex",
                flexDirection: collapsed ? "column" : "row",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "space-between",
                borderBottom: `1px solid ${COLORS.divider}`,
                marginBottom: "8px",
            }}
        >
            <IconButton
                sx={{
                    color: COLORS.primary,
                    mt: 1,
                }}
                onClick={handleToggle}
            >
                <MenuOutlinedIcon/>
            </IconButton>
            <img
                src={logo}
                alt="Company Logo"
                style={{
                    height: "80px",
                    width: "auto",
                }}
            />

        </Box>
    );

    // Custom User Profile Component
    const UserProfile = () => (
        <Box
            sx={{
                padding: collapsed ? "8px" : "16px",
                display: "flex",
                flexDirection: collapsed ? "column" : "row",
                alignItems: "center",
                gap: collapsed ? 1 : 2,
                borderBottom: `1px solid ${COLORS.divider}`,
                marginBottom: "16px",
            }}
        >
            <Avatar
                sx={{
                    bgcolor: COLORS.primary,
                    width: collapsed ? 36 : 40,
                    height: collapsed ? 36 : 40,
                }}
            >
                {userName.charAt(0)}
            </Avatar>

            {!collapsed && (
                <Box sx={{flex: 1, overflow: "hidden"}}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            color: COLORS.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {userName}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: COLORS.textLight,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {userRole}
                    </Typography>
                </Box>
            )}

            {collapsed && (
                <Tooltip title={`${userName} - ${userRole}`} placement="right">
                    <Box></Box>
                </Tooltip>
            )}
        </Box>
    );

    const renderMenuItems = () => {
        // Don't render menu until permissions are loaded for sub-users
        const subUserId = getSubUserId();
        if (!permissionsLoaded && subUserId) {
            return null;
        }

        return (
            <>
                {/* Dashboard - always visible */}
                {hasPermission("view_dashboard") && (
                    <MenuItem
                        active={isActive("/home")}
                        component={<Link to="/home"/>}
                        icon={<DashboardOutlined/>}
                    >
                        Dashboard
                    </MenuItem>
                )}

                {/* Analytics */}
                {hasPermission("view_analytics") && (
                    <MenuItem
                        active={isActive("/analytics")}
                        component={<Link to="/analytics"/>}
                        icon={<AutoGraphOutlined/>}
                    >
                        Analytics
                    </MenuItem>
                )}

                {/* Tenants SubMenu */}
                {(hasPermission("view_tenants") || hasPermission("view_vacating_tenants")) && (
                    <SubMenu
                        label="Tenants"
                        icon={<GroupsOutlined/>}
                        onOpenChange={() => handleSubmenuExpand('tenants')}
                        open={expandedSubmenus.tenants}
                        active={isActive(["tenant", "tenantList", "vacatingTenantList", "oldTenantList"])}
                    >
                        {hasPermission("view_tenants") && (
                            <MenuItem
                                active={isActive("/tenantList")}
                                component={<Link to="/tenantList"/>}
                                icon={<PeopleOutlined/>}
                            >
                                All Tenants
                            </MenuItem>
                        )}
                        {hasPermission("view_vacating_tenants") && (
                            <MenuItem
                                active={isActive("/vacatingTenantList")}
                                component={<Link to="/vacatingTenantList"/>}
                                icon={<PeopleOutlined/>}
                            >
                                Vacating Tenants
                            </MenuItem>
                        )}
                       {/* {hasPermission("view_tenants") && (
                            <MenuItem
                                active={isActive("/oldTenantList")}
                                component={<Link to="/oldTenantList"/>}
                                icon={<ElderlyOutlined/>}
                            >
                                Old Tenants
                            </MenuItem>
                        )}*/}
                    </SubMenu>
                )}

                {/* Properties */}
                {hasPermission("view_properties") && (
                    <MenuItem
                        active={isActive("/propertyList")}
                        component={<Link to="/propertyList"/>}
                        icon={<BusinessOutlined/>}
                    >
                        Properties
                    </MenuItem>
                )}

                {/* Finance SubMenu */}
                {(hasPermission("view_transactions") || hasPermission("view_expenses") || hasPermission("view_payouts")) && (
                    <SubMenu
                        label="Finance"
                        icon={<MonetizationOnOutlined/>}
                        onOpenChange={() => handleSubmenuExpand('money')}
                        open={expandedSubmenus.money}
                        active={isActive(["transactions", "expenses", "payouts"])}
                    >
                        {hasPermission("view_transactions") && (
                            <MenuItem
                                active={isActive("/transactions")}
                                component={<Link to="/transactions"/>}
                                icon={<PaymentsOutlined/>}
                            >
                                Transactions
                            </MenuItem>
                        )}
                        {hasPermission("view_expenses") && (
                            <MenuItem
                                active={isActive("/expenses")}
                                component={<Link to="/expenses"/>}
                                icon={<MoneyOffCsredOutlined/>}
                            >
                                Expenses
                            </MenuItem>
                        )}
                        {hasPermission("view_payouts") && (
                            <MenuItem
                                active={isActive("/payouts")}
                                component={<Link to="/payouts"/>}
                                icon={<AccountBalanceWalletOutlined/>}
                            >
                                Payouts
                            </MenuItem>
                        )}
                    </SubMenu>
                )}

                {/* Accounting */}
                {hasPermission("view_payment_reports") && (
                    <SubMenu
                        label="Accounting"
                        icon={<AccountBalanceOutlined/>}
                        onOpenChange={() => handleSubmenuExpand('accounting')}
                        open={expandedSubmenus.accounting}
                        active={isActive(["accounting"])}
                    >
                        <MenuItem
                            active={isActive("/accounting")}
                            component={<Link to="/accounting"/>}
                            icon={<AssessmentOutlined/>}
                        >
                            Overview
                        </MenuItem>
                        <MenuItem
                            active={isActive("/accounting/accounts")}
                            component={<Link to="/accounting/accounts"/>}
                            icon={<MenuBookOutlined/>}
                        >
                            Chart of Accounts
                        </MenuItem>
                        <MenuItem
                            active={isActive("/accounting/journal")}
                            component={<Link to="/accounting/journal"/>}
                            icon={<ReceiptOutlined/>}
                        >
                            Journal Entries
                        </MenuItem>
                        <SubMenu
                            label="Reports"
                            icon={<AssessmentOutlined/>}
                            onOpenChange={() => handleSubmenuExpand('accountingReports')}
                            open={expandedSubmenus.accountingReports}
                        >
                            <MenuItem
                                active={isActive("/accounting/reports/profit-and-loss")}
                                component={<Link to="/accounting/reports/profit-and-loss"/>}
                            >
                                Profit & Loss
                            </MenuItem>
                            <MenuItem
                                active={isActive("/accounting/reports/balance-sheet")}
                                component={<Link to="/accounting/reports/balance-sheet"/>}
                            >
                                Balance Sheet
                            </MenuItem>
                            <MenuItem
                                active={isActive("/accounting/reports/general-ledger")}
                                component={<Link to="/accounting/reports/general-ledger"/>}
                            >
                                General Ledger
                            </MenuItem>
                            <MenuItem
                                active={isActive("/accounting/reports/trial-balance")}
                                component={<Link to="/accounting/reports/trial-balance"/>}
                            >
                                Trial Balance
                            </MenuItem>
                            <MenuItem
                                active={isActive("/accounting/reports/income-statement")}
                                component={<Link to="/accounting/reports/income-statement"/>}
                            >
                                Income Statement
                            </MenuItem>
                        </SubMenu>
                        <MenuItem
                            active={isActive("/accounting/settings")}
                            component={<Link to="/accounting/settings"/>}
                            icon={<SettingsOutlined/>}
                        >
                            Settings
                        </MenuItem>
                    </SubMenu>
                )}

                {/* Tenant Issues */}
                {hasPermission("view_tenant_issues") && (
                    <MenuItem
                        active={isActive("/tenantIssues")}
                        component={<Link to="/tenantIssues"/>}
                        icon={<BusinessOutlined/>}
                    >
                        Tenant Issues
                    </MenuItem>
                )}

                {/* Backup */}
                {hasPermission("manage_backup") && (
                    <MenuItem
                        active={isActive("/backup")}
                        component={<Link to="/backup"/>}
                        icon={<BackupOutlined/>}
                    >
                        Backup
                    </MenuItem>
                )}

                {/* User Management - Only landlords or users with manage_users permission */}
                {hasPermission("manage_users") && (
                    <MenuItem
                        active={isActive("/userManagement")}
                        component={<Link to="/userManagement"/>}
                        icon={<PeopleOutlined/>}
                    >
                        User Management
                    </MenuItem>
                )}

                <Divider
                    sx={{
                        my: 2,
                        mx: collapsed ? 1 : 2,
                        borderColor: COLORS.divider,
                    }}
                />

                {/* Logout - always visible */}
                <MenuItem
                    onClick={logout}
                    icon={<LogoutOutlined style={{color: COLORS.secondary}}/>}
                >
                    Logout
                </MenuItem>
            </>
        );
    };

    // Don't render sidebar on login/signup pages
    if (["/", "/signup"].includes(location.pathname)) {
        return null;
    }

    return (
        <div
            style={{
                zIndex: 1000,
                height: "100vh",
                position: "fixed",
                left: 0,
                top: 0,
                bottom: 0,
                backgroundColor: "#FFFFFF",
                display: isMobile ? (sidebarOpen ? "block" : "none") : "block",
                // display: sidebarOpen ? "block" : "none",
            }}
        >
            <Sidebar
                style={{
                    height: "100%",
                    position: "relative",
                    border: "none",
                    boxShadow: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
                    backgroundColor: COLORS.background,
                    display: "flex",
                    flexDirection: "column",
                    flexShrink: 0,
                }}
                width="250px"
                collapsedWidth="70px"
                collapsed={!sidebarOpen}
            >
                <SidebarHeader/>
                <UserProfile/>

                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: COLORS.backgroundAlt,
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: COLORS.border,
                            borderRadius: "6px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            background: COLORS.textLight,
                        },
                    }}
                >
                    <Menu menuItemStyles={menuItemStyles}>
                        {renderMenuItems()}
                    </Menu>
                </Box>
            </Sidebar>
        </div>
    );
}